import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, AfterViewChecked } from '@angular/core';
import { Subscription, filter, pairwise, startWith } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { AiChatStateService, ChatMessage, ChatSession, SessionGroup } from './ai-chat-state.service';
import { AuthServiceService } from '../Auth/auth-service.service';
import { VoiceService } from './voice.service';
import { ScreenCaptureService } from './screen-capture.service';
import { ServiceService } from '../service.service';

interface QuickPrompt {
  icon: 'chart' | 'users' | 'inventory' | 'bulb';
  title: string;
  ask: string;
}

@Component({
  selector: 'app-ai-assistant',
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.css']
})
export class AiAssistantComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('threadEl') threadEl?: ElementRef<HTMLDivElement>;
  @ViewChild('composerEl') composerEl?: ElementRef<HTMLTextAreaElement>;

  // ── Local UI state ───────────────────────────────────────
  isOpen = false;
  isExpanded = false;
  isLoggedIn = false;
  onFullPage = false;
  composerFocused = false;
  inputText = '';
  unread = 0;

  // ── Welcome popup (shown big + animated right after login) ─────────
  welcomeVisible = false;
  welcomeGreeting = '';
  welcomeEmoji = '';
  welcomeSubline = '';
  private prevAuth: boolean | null = null;

  /** First name only, Title Case — "ATHUL RAJ PALAYI" → "Athul". */
  get welcomeFirstName(): string {
    const first = (this.employeeName || '').trim().split(/\s+/)[0] || 'there';
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }

  private presentWelcomePopup(): void {
    const h = new Date().getHours();
    if (h < 12)      { this.welcomeGreeting = 'Good morning';   this.welcomeEmoji = '☀️'; }
    else if (h < 17) { this.welcomeGreeting = 'Good afternoon'; this.welcomeEmoji = '🌤️'; }
    else             { this.welcomeGreeting = 'Good evening';   this.welcomeEmoji = '🌙'; }
    const sublines = [
      'How are you today? Your numbers are ready whenever you are.',
      'How\'s it going? I\'ve got all of today\'s data ready for you.',
      'Hope you\'re doing great — ask me anything about the business.',
      'How are you? Sales, stock, customers — just ask.'
    ];
    this.welcomeSubline = sublines[Math.floor(Math.random() * sublines.length)];
    // Small delay so the dashboard paints behind the popup first.
    setTimeout(() => { this.welcomeVisible = true; }, 700);
    try { sessionStorage.setItem('shajiWelcomed', '1'); } catch {}
  }

  dismissWelcome(): void { this.welcomeVisible = false; }

  openAiFromWelcome(): void {
    this.welcomeVisible = false;
    if (!this.onFullPage) { this.isOpen = true; this.isExpanded = true; this.unread = 0; }
  }

  // ── Auth ─────────────────────────────────────────────────
  // Read LIVE from localStorage on every access. The widget initializes on page
  // load (often BEFORE login in a fresh browser session) — caching these once in
  // ngOnInit froze empty values, causing 403s on /aiChat and chats saved under
  // the fallback name. Getters always reflect the current login.
  get userType(): string { return localStorage.getItem('userType') || ''; }
  get employeeName(): string { return localStorage.getItem('EmployeeName') || 'User'; }

  // ── Mirrored from service ────────────────────────────────
  sessionGroups: SessionGroup[] = [];
  messages: ChatMessage[] = [];
  currentSessionId: string | null = null;
  isLoading = false;
  isStreaming = false;
  suggestions: string[] = [];
  elapsedSecs = 0;
  quotaPercent = 0;
  quotaUnlimited = false;
  readonly quotaRingCircumference = 56.55;
  get quotaRingOffset(): number {
    return this.quotaRingCircumference - (this.quotaPercent / 100) * this.quotaRingCircumference;
  }


  /** Chip strip expand toggle — collapsed by default; resets on each new suggestions emission */
  chipsExpanded = false;
  /** Default number of chips to show when the strip is collapsed */
  readonly CHIP_VISIBLE_LIMIT = 2;

  // ── Voice state ──────────────────────────────────────────
  isListening = false;
  isSpeaking = false;
  /** Live interim text shown inside the voice popup while the user is speaking. */
  liveTranscript = '';
  /** Which message index is currently being spoken (null if none) */
  currentSpeakingIndex: number | null = null;
  /** Index of the message whose Copy button was just tapped (drives the "Copied" tick). */
  copiedIdx: number | null = null;
  /** Tracks whether the active listening session is the "product lookup" variant */
  productVoiceActive = false;

  // ── Report state ─────────────────────────────────────────
  isReporting = false;
  reportToast: { type: 'success' | 'error'; text: string } | null = null;

  /** OCR: when a photo is staged but not yet sent, holds the full data URL
   *  (for img src preview; backend gets the base64 portion at send time). */
  pendingPhotoDataUrl: string | null = null;
  pendingPhotoName: string | null = null;

  /** Uploaded document (PDF/Word/Excel): extracted + company-relevance-gated
   *  server-side. pendingDocId rides along with the next message; docBusy while
   *  extracting; docError holds a rejection reason to show under the composer. */
  pendingDocId: string | null = null;
  pendingDocName: string | null = null;
  docTruncated = false;
  docBusy = false;
  docError: string | null = null;

  /** Jump-to-latest pill: atBottom = the user is following the stream;
   *  showJumpBtn = new content arrived while they were scrolled up. */
  atBottom = true;
  showJumpBtn = false;

  get voiceSupported(): boolean { return this.voice.supported.recognition; }
  get speechSupported(): boolean { return this.voice.supported.synthesis; }

  /** OCR: file/camera input → re-encode/downscale → stash + auto-prompt if input empty. */
  async onPhotoAttached(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    input.value = '';
    this.pendingPhotoDataUrl = await this.normalizePhoto(file);
    this.pendingPhotoName = file.name || 'photo';
    if (!this.inputText.trim()) {
      this.inputText = 'Extract this invoice: vendor, date, line items, totals.';
      setTimeout(() => this.autoResizeRef(), 0);
    }
  }

  /** Re-encode an attached photo to a Bedrock-friendly JPEG and downscale it.
   *  iPhone photos arrive as HEIC and DSLR/phone shots are 8–12MP — both either
   *  exceed Bedrock's ~3.75MB base64 limit (silently dropped server-side) or are
   *  an unsupported format. Drawing through a canvas converts ANY browser-
   *  decodable image to JPEG and caps the longest edge at 2000px (ample for OCR).
   *  Any failure falls back to the original data URL so OCR never breaks. */
  private async normalizePhoto(file: File): Promise<string> {
    const MAX_EDGE = 2000;     // px — enough detail to read an invoice, small payload
    const QUALITY = 0.85;
    const rawDataUrl: string = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result as string);
      fr.onerror = () => reject(fr.error || new Error('read failed'));
      fr.readAsDataURL(file);
    });
    try {
      const img: HTMLImageElement = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error('decode failed'));
        im.src = rawDataUrl;
      });
      const w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return rawDataUrl;
      const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
      const cw = Math.round(w * scale), ch = Math.round(h * scale);
      const canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return rawDataUrl;
      ctx.drawImage(img, 0, 0, cw, ch);
      const out = canvas.toDataURL('image/jpeg', QUALITY);
      return (out && out.length > 100) ? out : rawDataUrl;
    } catch {
      return rawDataUrl;       // e.g. a HEIC this browser can't decode — let the server try
    }
  }

  /** Discard the currently-staged photo. */
  removePendingPhoto(): void {
    this.pendingPhotoDataUrl = null;
    this.pendingPhotoName = null;
  }

  /** Document attach (PDF / Word / Excel) → upload for server-side extraction +
   *  the company-relevance gate. Accepted → stage docId; rejected → show why. */
  async onDocAttached(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    input.value = '';
    this.removePendingPhoto();          // one attachment at a time
    this.removePendingDoc();
    this.pendingDocName = file.name || 'document';
    this.docBusy = true;
    try {
      const res = await this.state.uploadDocument(file, this.userType, this.employeeName);
      if (res && res.ok) {
        this.pendingDocId = res.docId;
        this.pendingDocName = res.filename || file.name;
        this.docTruncated = !!res.truncated;
        if (!this.inputText.trim()) {
          this.inputText = 'Summarise this document and pull out the key figures.';
          setTimeout(() => this.autoResizeRef(), 0);
        }
      } else {
        this.pendingDocName = null;
        this.docError = (res && res.message) || "I couldn't read that file.";
      }
    } catch {
      this.pendingDocName = null;
      this.docError = 'Upload failed — please check your connection and try again.';
    } finally {
      this.docBusy = false;
    }
  }

  /** Discard the staged document / clear any rejection notice. */
  removePendingDoc(): void {
    this.pendingDocId = null;
    this.pendingDocName = null;
    this.docError = null;
    this.docTruncated = false;
  }

  // Welcome quick-prompts (mirror full-page suggestions but compact)
  quickPrompts: QuickPrompt[] = [
    { icon: 'chart',     title: 'Sales overview',      ask: 'Give me a sales overview for this month' },
    { icon: 'users',     title: 'Top customers',       ask: 'Show me the top 10 customers by revenue this year' },
    { icon: 'inventory', title: 'Low stock alerts',    ask: 'Which products are running low on stock?' },
    { icon: 'bulb',      title: 'Salesperson targets', ask: 'Show each salesperson\'s target vs actual this month' },
  ];

  private shouldScroll = false;
  private subs: Subscription[] = [];

  constructor(
    public state: AiChatStateService,
    private auth: AuthServiceService,
    private router: Router,
    private voice: VoiceService,
    private screenCapture: ScreenCaptureService,
    private service: ServiceService
  ) {}

  // ── Lifecycle ────────────────────────────────────────────
  ngOnInit(): void {
    // userType / employeeName are live getters now — no one-time capture here.
    this.subs.push(this.auth.isAuthenticated.subscribe(v => {
      this.isLoggedIn = v;
      // Welcome popup: show on a REAL login (false → true transition), or when the
      // app opens already-logged-in in a fresh browser session (once per session).
      let welcomedThisSession = false;
      try { welcomedThisSession = !!sessionStorage.getItem('shajiWelcomed'); } catch {}
      if (v && (this.prevAuth === false || (this.prevAuth === null && !welcomedThisSession))) {
        this.presentWelcomePopup();
      }
      if (!v) this.welcomeVisible = false;
      this.prevAuth = v;
      if (!v) { this.isOpen = false; this.isExpanded = false; }
      else {
        this.state.loadSessions(this.employeeName);
        this.state.loadQuota(this.employeeName, this.userType);
      }
    }));

    // Hide the floating bubble on the dedicated full-page route
    this.evaluateRoute(this.router.url);
    this.subs.push(this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: any) => this.evaluateRoute(e.urlAfterRedirects)));

    this.subs.push(this.state.sessionGroups$.subscribe(g => this.sessionGroups = g));
    this.subs.push(this.state.messages$.subscribe(m => {
      this.messages = m;
      this.shouldScroll = true;
      // Bump unread when AI responds and panel is closed
      if (!this.isOpen && m.length && m[m.length - 1]?.role === 'ai' && m[m.length - 1]?.finalized) {
        this.unread++;
      }
    }));
    this.subs.push(this.state.currentSessionId$.subscribe(id => this.currentSessionId = id));
    this.subs.push(this.state.isLoading$.subscribe(v => this.isLoading = v));
    this.subs.push(this.state.isStreaming$.subscribe(v => this.isStreaming = v));
    // Scroll-on-suggestions: when follow-up chips become visible, the layout
    // shifts and the latest message can go behind them — force a re-scroll.
    this.subs.push(this.state.suggestions$.subscribe(s => {
      this.suggestions = s;
      // Each new AI response starts with a collapsed chip strip
      this.chipsExpanded = false;
      if (s && s.length > 0) this.shouldScroll = true;
    }));
    // Scroll-on-finalize: when streaming transitions true → false, pin the
    // user to the bottom so they see the "Done in Xs" summary and final text.
    this.subs.push(this.state.isStreaming$.pipe(
      startWith(false), pairwise()
    ).subscribe(([prev, curr]) => {
      if (prev === true && curr === false) this.shouldScroll = true;
    }));
    this.subs.push(this.state.elapsedSecs$.subscribe(s => this.elapsedSecs = s));
    this.subs.push(this.state.quotaPercent$.subscribe(p => this.quotaPercent = p));
    this.subs.push(this.state.quotaUnlimited$.subscribe(u => this.quotaUnlimited = u));    // Voice state mirroring (for CSS class bindings)
    this.subs.push(this.voice.isListening$.subscribe(v => {
      this.isListening = v;
      if (!v) {
        // Recognition ended — wipe interim text and the product-mode flag.
        this.liveTranscript = '';
        // productVoiceActive lingers a tick so the popup can fade out with the
        // right colour scheme; clear here once recognition is genuinely done.
        if (!this.isListening) this.productVoiceActive = false;
      }
    }));
    this.subs.push(this.voice.isSpeaking$.subscribe(v => {
      this.isSpeaking = v;
      if (!v) this.currentSpeakingIndex = null;
    }));
    // Live interim transcript — shown in the voice popup while user speaks.
    this.subs.push(this.voice.transcript$.subscribe(t => this.liveTranscript = t || ''));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    // Cancel any active voice activity when the component is destroyed
    try { this.voice.stopListening(); } catch {}
    try { this.voice.stopSpeaking(); } catch {}
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) { this.shouldScroll = false; this.scrollToBottom(); }
  }

  // ── Computed ─────────────────────────────────────────────
  get showWelcome(): boolean {
    // No real conversation: zero messages (service hasn't pushed welcome) or the
    // single welcome card with no follow-up. Once the user sends, hide.
    if (this.isLoading || this.isStreaming) return false;
    if (this.messages.length === 0) return true;
    if (this.messages.length === 1 && this.messages[0].role === 'ai'
        && this.messages[0].finalized && !this.currentSessionId) return true;
    return false;
  }

  get firstName(): string {
    return (this.employeeName || '').split(/\s+/)[0] || 'there';
  }

  get initials(): string {
    const parts = (this.employeeName || 'U').trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() || '').join('') || 'U';
  }

  // ── Open / close / expand ────────────────────────────────
  open(): void {
    this.isOpen = true;
    this.unread = 0;
    // Always refresh the sidebar for whoever's logged in right now.
    this.state.loadSessions(this.employeeName);
    // First open with nothing active → always land on a fresh "New chat"
    // (owner's rule 2026-06-14). A minimized-then-reopened conversation
    // (messages already present) is left untouched.
    if (this.messages.length === 0) {
      this.state.newChat(this.employeeName);         // fresh welcome view
    }
    setTimeout(() => this.composerEl?.nativeElement.focus(), 100);
  }

  close(): void {
    this.isOpen = false;
    this.isExpanded = false;
  }

  minimize(): void {
    this.isOpen = false;
  }

  // ── Actions ──────────────────────────────────────────────
  newChat(): void {
    this.atBottom = true;
    this.state.newChat(this.employeeName);
    this.inputText = '';
    setTimeout(() => this.composerEl?.nativeElement.focus(), 60);
  }

  openSession(s: ChatSession): void {
    this.atBottom = true;
    this.state.openSession(s, this.employeeName);
  }

  /** Sidebar: rename a chat (simple prompt — fine for an internal tool). */
  renameChat(s: ChatSession, ev?: Event): void {
    ev?.stopPropagation();
    const name = window.prompt('Rename this chat:', s.title || '');
    if (name && name.trim()) this.state.renameSession(s.session_id, name.trim(), this.employeeName);
  }

  /** Sidebar: delete a chat (confirm first). */
  deleteChat(s: ChatSession, ev?: Event): void {
    ev?.stopPropagation();
    if (window.confirm('Delete this chat? This can\'t be undone.')) {
      this.state.deleteSession(s.session_id, this.employeeName);
    }
  }

  send(): void {
    if (this.docBusy) return;   // wait for an in-flight document upload to finish
    const text = this.inputText.trim();
    if (!text) return;
    this.atBottom = true;       // a new turn always follows to the bottom
    this.inputText = '';
    // Extract raw base64 from the data URL (strip "data:image/...;base64," prefix).
    const dataUrl = this.pendingPhotoDataUrl;
    this.pendingPhotoDataUrl = null;
    this.pendingPhotoName = null;
    const comma = dataUrl ? dataUrl.indexOf(',') : -1;
    const base64 = (dataUrl && comma >= 0) ? dataUrl.slice(comma + 1) : null;
    const docId = this.pendingDocId;
    this.removePendingDoc();
    this.state.sendMessage(text, this.userType, this.employeeName, base64, docId);
    setTimeout(() => this.autoResizeRef(), 0);
  }

  stop(): void { this.state.stopStreaming(); }

  selectSuggestion(text: string): void {
    this.inputText = text;
    this.send();
  }

  toggleSteps(idx: number): void { this.state.toggleStepsOpen(idx); }

  // ── Chart type switcher ──────────────────────────────────
  /**
   * Returns the interchangeable chart types the user can switch to.
   * We offer the same set regardless of series count — ECharts handles
   * single-series gracefully for stacked/radar (one shape, no legend).
   */
  availableChartTypes(_currentType?: string): Array<{ id: string; label: string; icon: string }> {
    const labels: Record<string, [string, string]> = {
      bar:           ['Bar',     '▮'],
      line:          ['Line',    '╱'],
      area:          ['Area',    '◢'],
      horizontalBar: ['H-Bar',   '▬'],
      pie:           ['Pie',     '◔'],
      donut:         ['Donut',   '◍'],
      stackedBar:    ['Stacked', '▤'],
      scatter:       ['Scatter', '⋮⋮'],
      radar:         ['Radar',   '✦']
    };
    return ['bar', 'line', 'area', 'horizontalBar', 'pie', 'donut', 'stackedBar', 'scatter', 'radar']
      .map(id => ({ id, label: labels[id][0], icon: labels[id][1] }));
  }

  /** Swap the chart type for an existing AI message and rebuild ECharts options. */
  switchChartType(msgIndex: number, newType: string): void {
    const msg = this.messages[msgIndex];
    if (!msg?.chartData) return;
    const newData = { ...msg.chartData, type: newType as any };
    const newOpts = this.state.buildChartOptions(newData);
    // Use the public patchMessage method (cleaner than casting _messages$ as any).
    this.state.patchMessage(msgIndex, { chartData: newData, chartOptions: newOpts });
    // Let ECharts re-measure after Angular flushes the DOM.
    setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
  }

  /** Number of chips hidden when the strip is collapsed (used by "+N more" pill) */
  get hiddenChipsCount(): number {
    return Math.max(0, this.suggestions.length - this.CHIP_VISIBLE_LIMIT);
  }

  /** Chips actually rendered — first N when collapsed, all when expanded */
  get visibleChips(): string[] {
    if (this.chipsExpanded) return this.suggestions;
    return this.suggestions.slice(0, this.CHIP_VISIBLE_LIMIT);
  }

  toggleChipsExpanded(): void {
    this.chipsExpanded = !this.chipsExpanded;
    this.shouldScroll = true;
  }

  // ── Voice ────────────────────────────────────────────────
  /** True while we're waiting on the Groq transcript response. */
  isTranscribing = false;
  /** Currently selected speech-recognition language. Persisted in localStorage. */
  voiceLang: string = localStorage.getItem('shajiVoiceLang') || 'multi';
  /** Whether the language picker dropdown in the popup is open. */
  langPickerOpen = false;
  readonly voiceLangs = [
    { code: 'multi', label: 'English + Hindi', flag: '🌐' },
    { code: 'ar',    label: 'Arabic',          flag: '🇸🇦' },
    { code: 'en',    label: 'English only',    flag: '🇬🇧' },
    { code: 'hi',    label: 'Hindi only',      flag: '🇮🇳' }
  ];
  get voiceLangLabel(): string {
    return this.voiceLangs.find(l => l.code === this.voiceLang)?.label || 'English + Hindi';
  }
  get voiceLangFlag(): string {
    return this.voiceLangs.find(l => l.code === this.voiceLang)?.flag || '🌐';
  }
  selectVoiceLang(code: string): void {
    this.voiceLang = code;
    localStorage.setItem('shajiVoiceLang', code);
    this.langPickerOpen = false;
  }

  /** Dismiss the live voice popup — cancels recording, no transcribe, no send. */
  closeVoicePopup(): void {
    try { this.voice.stopListening(); } catch {}
    this.liveTranscript = '';
    this.productVoiceActive = false;
    this.isTranscribing = false;
  }

  /** Stop the live stream, get the final transcript, fill composer, send. */
  async confirmVoice(): Promise<void> {
    if (this.isTranscribing) return;
    this.isTranscribing = true;
    this.voice.setTranscribingState(true);
    const text = (await this.voice.stopRecording() || '').trim();
    this.isTranscribing = false;
    this.voice.setTranscribingState(false);
    if (!text) {
      this.liveTranscript = '(Could not catch that — try again)';
      this.voice.setTranscript(this.liveTranscript);
      setTimeout(() => { this.voice.hidePopup(); this.productVoiceActive = false; }, 1200);
      return;
    }
    this.liveTranscript = text;
    this.voice.setTranscript(text);
    setTimeout(() => {
      this.inputText = this.productVoiceActive ? ('Look up product: ' + text) : text;
      this.voice.hidePopup();
      this.productVoiceActive = false;
      setTimeout(() => this.autoResizeRef(), 0);
      if (!this.isLoading && !this.isStreaming) this.send();
    }, 250);
  }

  /** Toggle live dictation. Words appear in the popup as you speak; click
   *  Send (or the mic again) to stop and submit. */
  async toggleListening(): Promise<void> {
    if (!this.voiceSupported) return;
    if (this.isListening || this.isTranscribing) { await this.confirmVoice(); return; }
    this.productVoiceActive = false;
    this.liveTranscript = '';
    await this.voice.startListening({ userName: this.employeeName, userType: this.userType, language: this.voiceLang });
  }

  /** Live voice product search — prefixes "Look up product: " before send. */
  async startProductVoice(): Promise<void> {
    if (!this.voiceSupported) return;
    if (this.isListening || this.isTranscribing) { await this.confirmVoice(); return; }
    this.productVoiceActive = true;
    this.liveTranscript = '';
    await this.voice.startListening({ userName: this.employeeName, userType: this.userType, language: this.voiceLang });
  }

  /** Regenerate the latest answer — re-asks the same question for a fresh reply. */
  regenerate(): void {
    if (this.isStreaming || this.isLoading) return;
    this.atBottom = true;
    this.state.regenerateLast(this.userType, this.employeeName);
  }

  /** Tap-to-drill options menu (popover) state. */
  drillMenu: { visible: boolean; x: number; y: number; value: string; options: { label: string; prompt: string }[] } =
    { visible: false, x: 0, y: 0, value: '', options: [] };

  /** Run a chosen drill option — send its prompt as a fresh question. */
  runDrill(opt: { label: string; prompt: string }): void {
    this.drillMenu.visible = false;
    if (!opt?.prompt) return;
    this.inputText = opt.prompt;
    setTimeout(() => this.autoResizeRef(), 0);
    if (!this.isLoading && !this.isStreaming) this.send();
  }
  closeDrill(): void { this.drillMenu.visible = false; }

  /** Click delegation on an AI bubble — drives drill-down on currency spans.
   *  Innerhtml-rendered children don't fire Angular (click) bindings, so we
   *  listen on the parent and dispatch by class. */
  onMessageClick(e: MouseEvent, msg: any): void {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.classList && target.classList.contains('ai-drill')) {
      e.stopPropagation();
      const value = target.getAttribute('data-drill') || target.textContent || '';
      if (!value) return;
      const type = target.getAttribute('data-drill-type') || 'amount';
      const options = this.state.drillOptions(type, value, msg?.text || '');
      if (!options.length) return;
      // Anchor the menu near the tap, clamped to the viewport (mobile-safe).
      const MW = 230, MH = Math.min(options.length * 46 + 38, 280);
      const x = Math.max(8, Math.min(e.clientX, window.innerWidth - MW - 8));
      const y = (e.clientY + MH > window.innerHeight - 8) ? Math.max(8, e.clientY - MH) : e.clientY + 6;
      this.drillMenu = { visible: true, x, y, value, options };
      return;
    }
    if (target.classList && target.classList.contains('ai-action')) {
      e.stopPropagation();
      const label = target.getAttribute('data-action') || target.textContent || '';
      if (!label) return;
      // Send the action label as a fresh user message — the AI sees it like typing.
      this.inputText = label;
      setTimeout(() => this.autoResizeRef(), 0);
      if (!this.isLoading && !this.isStreaming) this.send();
    }
  }

  /** Copy an AI answer to the clipboard as clean text; flash a "Copied" tick for ~1.5s. */
  async copyMsg(index: number, text: string): Promise<void> {
    const ok = await this.state.copyMessageText(text);
    if (ok) {
      this.copiedIdx = index;
      setTimeout(() => { if (this.copiedIdx === index) this.copiedIdx = null; }, 1500);
    }
  }

  /** Speak (or stop) the given AI message text */
  toggleSpeak(messageText: string, index: number): void {
    if (!this.speechSupported) return;
    // If this same message is already being spoken, just stop.
    if (this.isSpeaking && this.currentSpeakingIndex === index) {
      this.voice.stopSpeaking();
      this.currentSpeakingIndex = null;
      return;
    }
    const clean = this.stripMarkup(messageText || '');
    if (!clean) return;
    this.currentSpeakingIndex = index;
    this.voice.speak(clean, { onEnd: () => this.currentSpeakingIndex = null });
  }

  /** Strip markdown, HTML, table pipes, code backticks, and emoji ranges so TTS sounds natural */
  stripMarkup(s: string): string {
    if (!s) return '';
    let out = s;
    // HTML tags
    out = out.replace(/<[^>]+>/g, ' ');
    // Code blocks and inline code
    out = out.replace(/```[\s\S]*?```/g, ' ');
    out = out.replace(/`([^`]*)`/g, '$1');
    // Bold / italic markers (**text**, *text*, __text__, _text_)
    out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
    out = out.replace(/\*([^*]+)\*/g, '$1');
    out = out.replace(/__([^_]+)__/g, '$1');
    out = out.replace(/_([^_]+)_/g, '$1');
    // Markdown links [label](url) → label
    out = out.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    // Headings and list markers
    out = out.replace(/^\s{0,3}#{1,6}\s+/gm, '');
    out = out.replace(/^\s*[-*+]\s+/gm, '');
    out = out.replace(/^\s*\d+\.\s+/gm, '');
    // Table pipes and separators
    out = out.replace(/\|/g, ' ');
    out = out.replace(/^[\s:-]+$/gm, ' ');
    // Emoji and pictographic ranges
    out = out.replace(/[\u{1F300}-\u{1FAFF}]/gu, ' ');
    out = out.replace(/[\u{2600}-\u{27BF}]/gu, ' ');
    out = out.replace(/[\u{1F000}-\u{1F2FF}]/gu, ' ');
    out = out.replace(/[\u{FE0F}\u{200D}]/gu, '');
    // Collapse whitespace
    out = out.replace(/\s+/g, ' ').trim();
    return out;
  }

  // ── Input ────────────────────────────────────────────────
  onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!this.isLoading && !this.isStreaming) this.send();
    }
  }

  autoResize(e: Event): void {
    const el = e.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }

  private autoResizeRef(): void {
    const el = this.composerEl?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }

  // Esc: collapse first, then close
  @HostListener('window:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      if (this.isExpanded) this.isExpanded = false;
      else this.close();
    }
  }

  // ── Helpers ──────────────────────────────────────────────
  trackByIndex(_: number, __: any): any { return _; }

  private scrollToBottom(): void {
    try {
      const el = this.threadEl?.nativeElement;
      if (!el) return;
      if (this.atBottom) {
        el.scrollTop = el.scrollHeight;     // follow the stream
        this.showJumpBtn = false;
      } else {
        this.showJumpBtn = true;            // user scrolled up — don't yank; offer a pill
      }
    } catch {}
  }

  /** Track whether the user is near the bottom (so streaming doesn't yank them up). */
  onThreadScroll(): void {
    const el = this.threadEl?.nativeElement;
    if (!el) return;
    const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
    this.atBottom = nearBottom;
    if (nearBottom) this.showJumpBtn = false;
  }

  /** Jump-to-latest pill → snap to the newest message and resume following. */
  jumpToLatest(): void {
    this.atBottom = true;
    this.showJumpBtn = false;
    this.scrollToBottom();
  }

  private evaluateRoute(url: string): void {
    this.onFullPage = !!url && /\/ai-assistant\b/.test(url);
    if (this.onFullPage) { this.isOpen = false; this.isExpanded = false; }
  }

  // ── Report an issue ──────────────────────────────────────
  reportModalOpen = false;
  reportText = '';
  /** Can only report once there's a real conversation (at least one user message). */
  get canReport(): boolean { return this.messages.some(m => m.role === 'user'); }

  // Clicking Report opens a popup asking what the issue is (unless the chat is empty).
  openReportPanel(): void {
    if (this.isReporting) return;
    if (!this.canReport) {
      this.reportToast = { type: 'error', text: 'Start a conversation first — there\'s nothing to report yet.' };
      setTimeout(() => { this.reportToast = null; }, 3500);
      return;
    }
    this.reportText = '';
    this.reportModalOpen = true;
  }

  cancelReport(): void {
    this.reportModalOpen = false;
    this.reportText = '';
  }

  // Capture the screen + send the report WITH the user's description.
  async submitReport(): Promise<void> {
    if (this.isReporting) return;
    const desc = this.reportText.trim();
    if (!desc) return;                       // description is required
    this.reportModalOpen = false;
    this.isReporting = true;
    this.reportToast = { type: 'success', text: 'Capturing screen…' };
    try {
      const cap = await this.screenCapture.capture();
      this.reportToast = { type: 'success', text: 'Sending report…' };
      await this.service.reportIssue(
        this.currentSessionId,
        this.employeeName,
        this.messages,
        cap.imageBase64 || undefined,
        cap.context,
        desc
      ).toPromise();
      this.reportToast = { type: 'success', text: 'Report sent. Thank you!' };
      this.reportText = '';
    } catch (e) {
      console.error('[Report] failed', e);
      this.reportToast = { type: 'error', text: 'Could not send report. Try again later.' };
    } finally {
      this.isReporting = false;
      setTimeout(() => { this.reportToast = null; }, 4000);
    }
  }
}
